import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

/* The Landing Pages library draws on the Video Ads library's intro, grid and card, so the Marketing libraries read as
   one system. Its own pieces: the "Live on your domain" section head, the card's path / address / offer rows, and the
   "no landing domain yet" note. */

export { PosterEmpty, PosterPlaceholder } from '../AdLibraryShell/AdLibraryShell.styles';
export { Minting, MintingText } from '../PlayableLibrary/PlayableLibrary.styles';
export { ConfirmBody, ConfirmFoot } from '../LandingPageComposer/LandingPageComposer.styles';

export {
  Body,
  EmptyNote,
  Intro,
  IntroHead,
  SavedBody as CardBody,
  SavedCard as Card,
  SavedGrid as Grid,
  SavedMeta as Meta,
  SavedName as CardTitle,
  Title,
} from '../VideoLibrary/VideoLibrary.styles';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const SectionTitle = styled(Text).attrs({ tag: 'h3' as const })`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

/** The landing host the pages are served from, beside the section title. */
export const HostTag = styled(Text).attrs({ tag: 'span' as const })`
  padding: 2px ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** The page's path on the landing host. */
export const CardPath = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The full address with its copy and open-in-a-new-tab controls. */
export const UrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  min-width: 0;
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[8]};
  border-radius: 8px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const UrlText = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Opens the live page in a new tab (an owner's own visit, never a preview). */
export const OpenLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: ${COLORS.textMuted};
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }
`;

/** The offer the page promotes. */
export const OfferRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  min-width: 0;
  color: ${COLORS.textFaint};
`;

/** The brand has no landing domain yet: a quiet panel in place of the grid. */
export const NoDomain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
  padding: ${SPACE[16]};
  border-radius: 14px;
  border: 1px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textMuted};
`;

export const NoDomainCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const NoDomainTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const NoDomainText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** The read failed: the reason over a retry. */
export const ErrorRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[8]};
`;

/** A draft card's publication line: a status dot and where (or whether) the page is live. */
export const PubRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  padding-top: ${SPACE[8]};
  border-top: 1px solid ${COLORS.hairline};
`;

export const PubDot = styled.span<{ $tone: 'live' | 'off' | 'failed' }>`
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${(p) =>
    p.$tone === 'live' ? STATUS_COLORS.positive : p.$tone === 'failed' ? STATUS_COLORS.negative : COLORS.textFaint};
`;

export const PubText = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** The saved pages' section, above the live ones. */
export const DraftsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

/* The view-only sheet of a live page made outside the Studio: a note, its address and offer, then its preview. */
export const Viewer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const ViewerFacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/* The preview fills a fixed-height column: its stage is flex-sized (min 360px) and needs a bounded parent. */
export const ViewerPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  height: min(68vh, 720px);
  min-height: 420px;
`;

/** A Studio card's count of the ads' tracking links landing on the page, at the end of its publication line. */
/** The card's "N ad links" chip: opens the sheet listing those links. */
export const LinkChip = styled.button`
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: ${SPACE[4]};
  margin-left: auto;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textMuted};
  font: inherit;
  cursor: pointer;
  transition:
    background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }
`;

export const LinkChipText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

/** The template step of a page made for an asset: the bar over the picker. */
export const NewForAsset = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/** The unpublish-and-archive confirmation: its body, then the tracking links it would cut off. */
export const ConfirmStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;
